import type { IProjectGroupWithProjects } from "src/features/projectGroups/state/projectGroupsSlice";
import type { ITagConfig } from "../../../model/unityObject";

const hasKey = (item: object, key: string): item is Record<string, unknown> => key in item;

const readField = (item: object, key: string): unknown =>
  hasKey(item, key) ? item[key] : undefined;

const readString = (item: object, key: string): string | undefined => {
  const value = readField(item, key);
  return typeof value === "string" ? value : undefined;
};

const readNumberOrString = (item: object, key: string): number | string | undefined => {
  const value = readField(item, key);
  return typeof value === "number" || typeof value === "string" ? value : undefined;
};

const readArray = (item: object, key: string): unknown[] | undefined => {
  const value = readField(item, key);
  return Array.isArray(value) ? value : undefined;
};

const readObject = (item: object, key: string): object | undefined => {
  const value = readField(item, key);
  return typeof value === "object" && value !== null ? value : undefined;
};

const prettifyFormat = (format: string) => {
  switch (format) {
    case "image":
    case "audio":
      return format.replace(/^\w/, (c) => c.toUpperCase());
    case "model3d":
      return "3D Model";
    case "video":
      return "video";
    default:
      return format;
  }
};

export interface OrganizationTag {
  id: number;
  name: string;
  organization_id?: { id: number } | null;
}

export const getTagsOrganizationMedia = (
  listTags: ReadonlyArray<OrganizationTag>,
  orgaId: number,
): ITagConfig[] => {
  return listTags
    .filter((tag) => Number(tag.organization_id?.id) === Number(orgaId))
    .map((tag) => ({ id: tag.id, name: tag.name }));
};

const dateValueOf = (item: object): number => {
  const updatedAtTs = readNumberOrString(item, "updated_at_ts");
  if (updatedAtTs !== undefined) {
    return Number(updatedAtTs);
  }
  return new Date(readString(item, "created_at") ?? "").getTime();
};

export const sortByDate = <T extends object>(
  list: readonly T[],
  direction: "asc" | "desc",
): T[] => {
  return [...list].sort((elem1, elem2) => {
    const firstElement = direction === "desc" ? elem2 : elem1;
    const secondElement = direction === "desc" ? elem1 : elem2;

    return dateValueOf(firstElement) - dateValueOf(secondElement);
  });
};

const sizeInBytesOf = <T extends object>(item: T): number =>
  "size_in_bytes" in item && typeof item.size_in_bytes === "number" ? item.size_in_bytes : 0;

export const sortBySize = <T extends object>(list: T[], direction: "asc" | "desc"): T[] => {
  return [...list].sort((elem1: T, elem2: T) => {
    const firstElement = direction === "desc" ? elem2 : elem1;
    const secondElement = direction === "desc" ? elem1 : elem2;
    return sizeInBytesOf(firstElement) - sizeInBytesOf(secondElement);
  });
};

export const sortByAlpha = <T extends object>(
  list: readonly T[],
  direction: "asc" | "desc",
): T[] => {
  return [...list].sort((elem1, elem2) => {
    const firstElement = direction === "desc" ? elem2 : elem1;
    const secondElement = direction === "desc" ? elem1 : elem2;
    const firstName = readString(firstElement, "name") ?? "";
    const secondName = readString(secondElement, "name") ?? "";
    return firstName.toLowerCase().localeCompare(secondName.toLowerCase());
  });
};

const pickSortValue = (elem: object, propertyName: string, isMediaList: boolean): unknown => {
  let value = readField(elem, propertyName);

  if (propertyName === "permissions") {
    value = readArray(elem, "permissions")?.length;
  }
  if (isMediaList) {
    value =
      propertyName === "Type"
        ? prettifyFormat(readString(elem, "media_360_tag") ?? "")
        : readField(elem, "type");
  }
  if (propertyName === "projectGroupId") {
    value = readArray(elem, "permissions")?.length;
  }

  return value;
};

export const sortByAlphabeticalOrder = <T extends object>(
  list: readonly T[],
  propertyName: string,
  direction: "asc" | "desc",
  isMediaList = false,
): T[] => {
  return [...list].sort((elem1, elem2) => {
    const property1 = pickSortValue(elem1, propertyName, isMediaList);
    const property2 = pickSortValue(elem2, propertyName, isMediaList);

    if (direction === "asc") {
      if (String(property1) < String(property2)) {
        return -1;
      }
      if (String(property1) > String(property2)) {
        return 1;
      }
    } else if (direction === "desc") {
      if (String(property1) > String(property2)) {
        return -1;
      }
      if (String(property1) < String(property2)) {
        return 1;
      }
    }
    return 0;
  });
};

export const filterGroupResults = (
  list: Array<IProjectGroupWithProjects>,
  filter: string[],
): Array<IProjectGroupWithProjects> => {
  let filteredList = [...list];
  if (filter) {
    const direction: "asc" | "desc" = filter[1] === "desc" ? "desc" : "asc";
    if (filter[0] === "alphanum") {
      filteredList = sortByAlpha(filteredList, direction);
    } else {
      filteredList = sortByDate(filteredList, direction);
    }
  }

  return filteredList;
};

/**
 * Rank lookup for the sort comparators below. They used to `findIndex` into
 * the ordered array on both sides of every comparison, which made the sorts
 * O(n^2 log n); this precomputes the ranks in one pass.
 *
 * First occurrence wins and misses resolve to -1, exactly like `findIndex`.
 */
const buildRankMap = <K>(ordered: readonly object[], pick: (item: object) => K): Map<K, number> => {
  const ranks = new Map<K, number>();
  ordered.forEach((item, index) => {
    const key = pick(item);
    if (!ranks.has(key)) ranks.set(key, index);
  });
  return ranks;
};
const rankOf = <K>(ranks: Map<K, number>, value: K): number => ranks.get(value) ?? -1;

export const sortByStatus = <T extends object>(
  list: readonly T[],
  direction: "asc" | "desc",
  listStatus: ReadonlyArray<{ id: number | undefined; status: string }>,
): T[] => {
  const statusOrder = sortByAlphabeticalOrder(listStatus, "status", direction);
  const ranks = buildRankMap(statusOrder, (item) => readField(item, "id"));

  return [...list].sort((elem1, elem2) => {
    const property1 = rankOf(ranks, readField(elem1, "id"));
    const property2 = rankOf(ranks, readField(elem2, "id"));

    if (direction === "asc") {
      if (String(property1) < String(property2)) {
        return -1;
      }
      if (String(property1) > String(property2)) {
        return 1;
      }
    } else if (direction === "desc") {
      if (String(property1) > String(property2)) {
        return -1;
      }
      if (String(property1) < String(property2)) {
        return 1;
      }
    }
    return 0;
  });
};

export const sortByUsers = <T extends object>(
  list: readonly T[],
  direction: "asc" | "desc",
  propertyName: string,
  isStats = false,
): T[] => {
  const mappingIdAndUser: Array<{ id: unknown; user: unknown }> = list.map((elem) => {
    const userObj = readObject(elem, isStats ? "enduser" : "user");
    return {
      id: readField(elem, "id"),
      user: userObj ? readField(userObj, propertyName) : undefined,
    };
  });

  const statusOrder = sortByAlphabeticalOrder(mappingIdAndUser, "user", "asc");
  const ranks = buildRankMap(statusOrder, (item) => readField(item, "user"));

  return [...list].sort((elem1, elem2) => {
    const userObj1 = readObject(elem1, isStats ? "enduser" : "user");
    const userObj2 = readObject(elem2, isStats ? "enduser" : "user");
    const property1 = rankOf(ranks, userObj1 ? readField(userObj1, propertyName) : undefined);
    const property2 = rankOf(ranks, userObj2 ? readField(userObj2, propertyName) : undefined);
    return direction === "asc" ? property1 - property2 : property2 - property1;
  });
};

export const sortByExtenxion = <T extends object>(
  list: readonly T[],
  direction: "asc" | "desc",
): T[] => {
  const mappingIdAndUser: Array<{ id: unknown; extension: string }> = list.map((elem) => ({
    id: readField(elem, "id"),
    extension: (readString(elem, "filename") ?? "").split(".").pop()?.toUpperCase() ?? "",
  }));
  const statusOrder = sortByAlphabeticalOrder(mappingIdAndUser, "extension", "asc");
  const ranks = buildRankMap(statusOrder, (item) => readField(item, "id"));

  return [...list].sort((elem1, elem2) => {
    const property1 = rankOf(ranks, readField(elem1, "id"));
    const property2 = rankOf(ranks, readField(elem2, "id"));
    return direction === "asc" ? property1 - property2 : property2 - property1;
  });
};
